import React from 'react'

/**
 * Renderiza texto de mensagem HUMANA no estilo WhatsApp (formatação leve),
 * preservando @menções e (via CSS do container) quebras de linha.
 *
 * Suporta: *negrito*, _itálico_, ~riscado~, `código`, links http(s) e @menções.
 * Um nível só (sem aninhar) — como o WhatsApp. Guardas de fronteira
 * (lookbehind/lookahead) evitam falsos positivos comuns (ex.: "2*3*4 = 24"
 * NÃO vira negrito). O conteúdo de `código` é literal (sem formatação dentro).
 */

const CODE_STYLE: React.CSSProperties = {
  fontFamily: 'var(--mantine-font-family-monospace, monospace)',
  background: 'var(--ai-input-bg, rgba(0,0,0,0.06))',
  padding: '0 4px',
  borderRadius: 4,
  fontSize: '0.9em',
}
const MENTION_STYLE: React.CSSProperties = {
  color: 'var(--mantine-color-teal-7)',
  fontWeight: 600,
}

// Fronteiras: abre após início/espaço/"("; fecha antes de espaço/pontuação/fim.
const OPEN = '(?<=^|[\\s(])'
const CLOSE = '(?=[\\s).,!?;:]|$)'

const TOKEN_RE = new RegExp(
  [
    '`([^`\\n]+)`', // 1 código
    `${OPEN}\\*(\\S(?:[^*\\n]*\\S)?)\\*${CLOSE}`, // 2 negrito
    `${OPEN}_(\\S(?:[^_\\n]*\\S)?)_${CLOSE}`, // 3 itálico
    `${OPEN}~(\\S(?:[^~\\n]*\\S)?)~${CLOSE}`, // 4 riscado
    '(https?:\\/\\/[^\\s]+)', // 5 link
    '(@[\\w.@-]+)', // 6 menção
  ].join('|'),
  'g',
)

export function renderRichText(body: string): React.ReactNode {
  if (!body) return null
  const nodes: React.ReactNode[] = []
  let last = 0
  let key = 0
  let m: RegExpExecArray | null
  TOKEN_RE.lastIndex = 0
  while ((m = TOKEN_RE.exec(body)) !== null) {
    if (m.index > last) nodes.push(body.slice(last, m.index))
    const [full, code, bold, italic, strike, link, mention] = m
    if (code != null) {
      nodes.push(<code key={key++} style={CODE_STYLE}>{code}</code>)
    } else if (bold != null) {
      nodes.push(<strong key={key++}>{bold}</strong>)
    } else if (italic != null) {
      nodes.push(<em key={key++}>{italic}</em>)
    } else if (strike != null) {
      nodes.push(<s key={key++}>{strike}</s>)
    } else if (link != null) {
      nodes.push(
        <a key={key++} href={link} target="_blank" rel="noopener noreferrer">
          {link}
        </a>,
      )
    } else if (mention != null) {
      nodes.push(
        <span key={key++} data-mention="1" style={MENTION_STYLE}>
          {mention}
        </span>,
      )
    } else {
      nodes.push(full)
    }
    last = m.index + full.length
  }
  if (last < body.length) nodes.push(body.slice(last))
  return nodes
}
